import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fileUrl, errMsg } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import ChatWidget from "../components/ChatWidget";
import { ShoppingBag, Plus, Minus, X, Flame, ArrowLeft, Utensils, CheckCircle2, Clock, ChefHat, PackageCheck, Banknote, CreditCard, BellRing, Bike, Camera, UserCircle } from "lucide-react";

const money = (v) => (v > 0 ? `₺${Number(v).toFixed(2)}` : "Fiyat için sorunuz");

const TRACK_STEPS = [
  { key: "pending", label: "Alındı", icon: Clock },
  { key: "preparing", label: "Hazırlanıyor", icon: ChefHat },
  { key: "ready", label: "Hazır", icon: PackageCheck },
  { key: "completed", label: "Tamamlandı", icon: CheckCircle2 },
];

export default function PublicMenu() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const tableMode = !!params.get("masa"); // QR masa görünümü => sadece görüntüleme
  const [data, setData] = useState({ categories: [], products: [], settings: {} });
  const [activeCat, setActiveCat] = useState("all");
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [address, setAddress] = useState("");
  const [note, setNote] = useState("");
  const [payment, setPayment] = useState("cash");
  const [busy, setBusy] = useState(false);
  const [myActive, setMyActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileOpen, setProfileOpen] = useState(false);

  useEffect(() => {
    api.get("/public/menu").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (user?.address) setAddress(user.address); }, [user]);

  const loadMine = async () => {
    if (user?.role !== "customer") return;
    try {
      const { data } = await api.get("/orders/mine");
      const active = data.find((o) => ["pending", "preparing", "ready"].includes(o.status));
      setMyActive(active || null);
    } catch {}
  };

  useEffect(() => {
    loadMine();
    const t = setInterval(loadMine, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [user]);

  const filtered = useMemo(() => {
    if (activeCat === "all") return data.products;
    return data.products.filter((p) => p.categoryId === activeCat);
  }, [activeCat, data.products]);

  const addToCart = (p) => {
    setCart((c) => {
      const ex = c.find((i) => i.productId === p.id);
      if (ex) return c.map((i) => (i.productId === p.id ? { ...i, qty: i.qty + 1 } : i));
      return [...c, { productId: p.id, name: p.name, price: p.price, qty: 1 }];
    });
    toast.success(`${p.name} sepete eklendi`);
  };

  const changeQty = (id, d) => {
    setCart((c) => c.map((i) => (i.productId === id ? { ...i, qty: Math.max(0, i.qty + d) } : i)).filter((i) => i.qty > 0));
  };

  const total = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const count = cart.reduce((s, i) => s + i.qty, 0);

  const checkout = async () => {
    if (cart.length === 0) return;
    if (!user) {
      toast.error("Sipariş için önce üye girişi yapmalısınız");
      nav("/giris");
      return;
    }
    if (user.role === "customer" && user.status !== "approved") {
      toast.error("Hesabınız henüz onaylanmadı. Verdiğiniz telefondan aranıp onaylandıktan sonra sipariş verebilirsiniz.");
      return;
    }
    if (!address.trim()) {
      toast.error("Lütfen teslimat adresinizi yazın");
      return;
    }
    setBusy(true);
    try {
      await api.post("/orders", {
        items: cart.map((i) => ({ ...i, options: [], note: "" })),
        type: "delivery",
        tableNo: null,
        address,
        note,
        paymentMethod: payment,
      });
      toast.success("Siparişiniz alındı! Kapıda ödeme ile hazırlanıyor 🎉");
      setCart([]);
      setCartOpen(false);
      setNote("");
      loadMine();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const pending = user?.role === "customer" && user?.status !== "approved";

  return (
    <div className="min-h-screen bg-[#0B0B0C]">
      {/* Top bar */}
      <div className="glass sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-zinc-300 hover:text-[#FFE599]" data-testid="menu-back-btn">
            <ArrowLeft size={18} />
            <img src="/cafein-icon.png" alt="CAFE-İN" className="h-9 w-9 object-contain" />
            <span className="font-serif text-xl gold-text hidden sm:block">CAFE-İN</span>
          </Link>
          {tableMode ? (
            <span className="flex items-center gap-2 text-sm font-mono px-4 py-2.5 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#FFE599]" data-testid="table-badge">
              <Utensils size={16} /> Masa {params.get("masa")}
            </span>
          ) : (
            <div className="flex items-center gap-2">
              {user && user.role === "customer" && (
                <button onClick={() => setProfileOpen(true)} className="btn-outline px-3 py-2.5 rounded-xl flex items-center gap-2 text-sm" data-testid="open-profile-btn">
                  <UserCircle size={18} /> <span className="hidden sm:inline">Hesabım</span>
                </button>
              )}
              <button onClick={() => setCartOpen(true)} className="btn-gold px-4 py-2.5 rounded-xl flex items-center gap-2 relative" data-testid="open-cart-btn">
                <ShoppingBag size={18} /> Sepet
                {count > 0 && <span className="absolute -top-2 -right-2 bg-[#0B0B0C] text-[#FFE599] text-xs font-mono h-6 w-6 rounded-full flex items-center justify-center border border-[#D4AF37]">{count}</span>}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Header */}
      <div className="max-w-6xl mx-auto px-5 pt-8 pb-4 text-center">
        <h1 className="font-serif text-4xl sm:text-5xl gold-text">Menü</h1>
        <p className="text-zinc-400 mt-2 text-sm">{data.settings?.name || "CAFE-İN"} • Osmangazi/Bursa</p>
      </div>

      {/* Table mode notice (view only) */}
      {tableMode && (
        <div className="max-w-6xl mx-auto px-5 mb-2">
          <div className="rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 px-4 py-4 text-center flex flex-col items-center gap-1.5" data-testid="table-view-note">
            <BellRing className="text-[#D4AF37]" size={22} />
            <p className="text-[#FFE599] text-sm font-medium">Masanızdan sipariş vermek için garsonumuzu çağırın</p>
            <p className="text-zinc-400 text-xs">Menümüzü inceleyin, seçiminizi yapın; siparişinizi garsonumuz sizin için girecektir.</p>
          </div>
        </div>
      )}

      {/* Paket promo (non-table) */}
      {!tableMode && (
        <div className="max-w-6xl mx-auto px-5 mb-2">
          <div className="rounded-xl bg-gradient-to-r from-[#D4AF37]/15 to-transparent border border-[#D4AF37]/25 px-4 py-3 flex items-center gap-3" data-testid="paket-promo">
            <Bike className="text-[#D4AF37] shrink-0" size={20} />
            <p className="text-sm text-zinc-200">Paket sipariş — <span className="text-[#FFE599]">aracı komisyonu yok</span>, doğrudan bizden. Ödeme <span className="text-[#FFE599]">kapıda nakit veya kart (POS)</span>.</p>
          </div>
        </div>
      )}

      {/* Approval banner */}
      {!tableMode && pending && (
        <div className="max-w-6xl mx-auto px-5 mb-2">
          <div className="rounded-xl bg-amber-950/40 border border-amber-500/30 px-4 py-3 text-amber-200 text-sm text-center" data-testid="approval-banner">
            Hesabınız onay bekliyor. Verdiğiniz telefondan aranıp onaylandıktan sonra sipariş verebilirsiniz.
          </div>
        </div>
      )}

      {/* Active order tracking */}
      {myActive && (
        <div className="max-w-6xl mx-auto px-5 mb-4">
          <div className="card-lux p-5" data-testid="order-tracking">
            <div className="flex items-center justify-between mb-4">
              <span className="font-serif text-lg text-white">Aktif Sipariş Takibi</span>
              <span className="font-mono text-xs text-zinc-400">#{myActive.id.slice(0, 8)}</span>
            </div>
            <div className="flex items-center justify-between">
              {TRACK_STEPS.map((step, i) => {
                const cur = TRACK_STEPS.findIndex((x) => x.key === myActive.status);
                const done = i <= cur;
                const Icon = step.icon;
                return (
                  <div key={step.key} className="flex-1 flex flex-col items-center relative">
                    {i > 0 && <div className={`absolute right-1/2 top-5 h-0.5 w-full ${i <= cur ? "bg-[#D4AF37]" : "bg-zinc-700"}`} />}
                    <div className={`relative z-10 h-10 w-10 rounded-full flex items-center justify-center ${done ? "btn-gold" : "bg-[#1E1E24] border border-zinc-700 text-zinc-500"}`}>
                      <Icon size={18} />
                    </div>
                    <span className={`mt-2 text-xs ${done ? "text-[#FFE599]" : "text-zinc-500"}`}>{step.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Category tabs */}
      <div className="max-w-6xl mx-auto px-5 py-4 flex gap-2 overflow-x-auto no-scrollbar sticky top-[60px] z-20 bg-[#0B0B0C]/80 backdrop-blur">
        <button onClick={() => setActiveCat("all")} className={`px-5 py-2 rounded-full text-sm whitespace-nowrap transition-all ${activeCat === "all" ? "btn-gold" : "btn-outline"}`} data-testid="cat-all">Tümü</button>
        {data.categories.map((c) => (
          <button key={c.id} onClick={() => setActiveCat(c.id)} className={`px-5 py-2 rounded-full text-sm whitespace-nowrap transition-all ${activeCat === c.id ? "btn-gold" : "btn-outline"}`} data-testid={`cat-${c.id}`}>{c.name}</button>
        ))}
      </div>

      {/* Real photo note */}
      <div className="max-w-6xl mx-auto px-5 pt-1">
        <div className="flex items-center gap-3 rounded-xl border border-[#D4AF37]/25 bg-gradient-to-r from-[#D4AF37]/10 to-transparent px-4 py-3" data-testid="real-photo-note">
          <div className="h-9 w-9 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center shrink-0">
            <Camera size={17} className="text-[#FFE599]" />
          </div>
          <p className="text-xs sm:text-sm text-zinc-300 leading-snug">
            <span className="text-[#FFE599] font-medium">Gördüğünüz her kare gerçek.</span> Fotoğraflar temsili değil, tam da mutfağımızdan çıkıp önünüze geleceği gibi — CAFE-İN lezzetlerinin bizzat kendisi.
          </p>
        </div>
      </div>

      {/* Products */}
      <div className="max-w-6xl mx-auto px-5 pb-32 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-4">
        {filtered.map((p) => (
          <div key={p.id} className="card-lux card-hover overflow-hidden flex flex-col" data-testid={`product-${p.id}`}>
            <div className="relative aspect-[4/3] overflow-hidden">
              {p.imageUrl ? (
                <img src={fileUrl(p.imageUrl)} alt={p.name} loading="lazy" decoding="async" className="h-full w-full object-cover object-center transition-transform duration-500 hover:scale-[1.06]" />
              ) : (
                <div className="h-full w-full flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-[#1b1b20] to-[#0d0d0f]">
                  <img src="/cafein-icon.png" alt="CAFE-İN" className="h-10 w-10 object-contain opacity-40" />
                  <span className="text-[10px] font-mono uppercase tracking-widest text-[#D4AF37]/40">CAFE-İN</span>
                </div>
              )}
              {p.isBestSeller && <span className="absolute top-3 left-3 flex items-center gap-1 text-[10px] font-mono px-2.5 py-1 rounded-full bg-[#0B0B0C]/85 backdrop-blur text-[#FFE599] border border-[#D4AF37]/40"><Flame size={11} />Popüler</span>}
            </div>
            <div className="p-4 flex flex-col flex-1">
              <h3 className="font-serif text-lg text-white leading-tight">{p.name}</h3>
              {p.description && <p className="text-xs text-zinc-400 mt-1.5 line-clamp-2">{p.description}</p>}
              <div className="flex items-center justify-between mt-auto pt-3">
                <span className="font-mono text-sm text-[#FFE599]">{money(p.price)}</span>
                {!tableMode && (
                  <button onClick={() => addToCart(p)} className="btn-gold h-9 px-4 rounded-lg flex items-center gap-1.5 text-sm" data-testid={`add-${p.id}`}>
                    <Plus size={16} /> Ekle
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        {!loading && filtered.length === 0 && <p className="text-zinc-500 col-span-2 text-center py-10">Bu kategoride ürün yok.</p>}
        {loading && (
          <div className="col-span-2 flex justify-center py-16" data-testid="menu-loading">
            <div className="w-10 h-10 border-2 border-[#D4AF37]/20 border-t-[#D4AF37] rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Cart drawer (only in paket mode) */}
      {!tableMode && cartOpen && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="cart-drawer">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setCartOpen(false)} />
          <div className="relative w-full max-w-md h-full bg-[#101013] border-l border-[#D4AF37]/20 flex flex-col slide-in-right">
            <div className="px-5 py-4 border-b border-[#D4AF37]/20 flex items-center justify-between">
              <h2 className="font-serif text-2xl gold-text">Paket Sepetim</h2>
              <button onClick={() => setCartOpen(false)} className="text-zinc-400 hover:text-white" data-testid="close-cart-btn"><X size={24} /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4 no-scrollbar">
              {cart.length === 0 && <p className="text-center text-zinc-500 mt-10">Sepetiniz boş</p>}
              {cart.map((i) => (
                <div key={i.productId} className="flex items-center gap-3 card-lux p-3" data-testid={`cart-item-${i.productId}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{i.name}</div>
                    <div className="font-mono text-xs text-[#FFE599]">{money(i.price)}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => changeQty(i.productId, -1)} className="h-7 w-7 rounded-lg btn-outline flex items-center justify-center" data-testid={`dec-${i.productId}`}><Minus size={14} /></button>
                    <span className="font-mono w-6 text-center text-white">{i.qty}</span>
                    <button onClick={() => changeQty(i.productId, 1)} className="h-7 w-7 rounded-lg btn-gold flex items-center justify-center" data-testid={`inc-${i.productId}`}><Plus size={14} /></button>
                  </div>
                </div>
              ))}

              {cart.length > 0 && (
                <div className="space-y-4 pt-2">
                  <div className="rounded-xl bg-amber-950/40 border border-amber-500/30 px-3 py-2.5 text-[11px] text-amber-200/90 leading-relaxed" data-testid="delivery-note">
                    Paket sipariş için üyelik gereklidir. Kayıt sonrası verdiğiniz telefondan aranıp üyeliğiniz onaylanır; ardından paket sipariş verebilirsiniz.
                  </div>
                  <div>
                    <label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Teslimat Adresi</label>
                    <textarea value={address} onChange={(e) => setAddress(e.target.value)} className="lux-input mt-2" rows={2} placeholder="Mahalle, cadde, no, kat / tarif" data-testid="address-input" />
                  </div>
                  <div>
                    <label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Ödeme (Kapıda)</label>
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => setPayment("cash")} className={`flex-1 py-2.5 rounded-xl text-sm flex items-center justify-center gap-2 ${payment === "cash" ? "btn-gold" : "btn-outline"}`} data-testid="pay-cash"><Banknote size={16} />Nakit</button>
                      <button onClick={() => setPayment("card")} className={`flex-1 py-2.5 rounded-xl text-sm flex items-center justify-center gap-2 ${payment === "card" ? "btn-gold" : "btn-outline"}`} data-testid="pay-card"><CreditCard size={16} />Kart (POS)</button>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Not (opsiyonel)</label>
                    <input value={note} onChange={(e) => setNote(e.target.value)} className="lux-input mt-2" placeholder="Örn: az soğanlı" data-testid="note-input" />
                  </div>
                </div>
              )}
            </div>

            {cart.length > 0 && (
              <div className="p-5 border-t border-[#D4AF37]/20 space-y-3">
                <div className="flex justify-between text-sm text-zinc-300"><span>Toplam</span><span className="font-mono text-lg text-[#FFE599]">₺{total.toFixed(2)}</span></div>
                <button onClick={checkout} disabled={busy || pending} className="btn-gold w-full py-3.5 rounded-xl text-base" data-testid="checkout-btn">
                  {pending ? "Hesabınız Onay Bekliyor" : busy ? "Gönderiliyor..." : "Siparişi Onayla"}
                </button>
                {!user && <p className="text-center text-xs text-zinc-500">Sipariş için üye <Link to="/giris" className="text-[#FFE599]">girişi</Link> gereklidir</p>}
              </div>
            )}
          </div>
        </div>
      )}

      {profileOpen && <ProfileModal user={user} onClose={() => setProfileOpen(false)} onSaved={refresh} />}

      <ChatWidget />
    </div>
  );
}

function ProfileModal({ user, onClose, onSaved }) {
  const [f, setF] = useState({ name: user?.name || "", phone: user?.phone || "", address: user?.address || "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/auth/me", f);
      toast.success("Bilgileriniz güncellendi");
      if (onSaved) await onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="profile-modal">
      <div className="card-lux w-full max-w-md scale-in">
        <div className="px-5 py-4 border-b border-[#D4AF37]/20 flex items-center justify-between">
          <h2 className="font-serif text-2xl gold-text">Hesabım</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="profile-close"><X size={24} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Ad Soyad</label>
            <input value={f.name} onChange={set("name")} className="lux-input mt-2" data-testid="profile-name" />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Telefon</label>
            <input value={f.phone} onChange={set("phone")} className="lux-input mt-2" data-testid="profile-phone" />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Kayıtlı Adres</label>
            <textarea value={f.address} onChange={set("address")} rows={3} className="lux-input mt-2 resize-none" placeholder="Paket siparişte otomatik dolar" data-testid="profile-address" />
            <p className="text-[11px] text-zinc-500 mt-1.5">Bu adres paket siparişlerinde otomatik gelir; sipariş sırasında yine değiştirebilirsiniz.</p>
          </div>
          <button onClick={save} disabled={busy} className="btn-gold w-full py-3 rounded-xl" data-testid="profile-save">{busy ? "Kaydediliyor..." : "Kaydet"}</button>
        </div>
      </div>
    </div>
  );
}
